import type * as React from 'react'

import { Notice } from '@buffer-mono/popcorn'

import type { FilterByChannel_Channel } from '~publish/components/FilterByChannel'
import type { ResultOf } from '~publish/graphql'

export type Service = ResultOf<typeof FilterByChannel_Channel>['service']

import type { JSX } from 'react'

type PausedChannel = {
  id: string
  name: string
  service: Service
}

type AllChannelsPausedQueueNoticeProps = Omit<
  React.ComponentPropsWithoutRef<typeof Notice>,
  'variant' | 'children'
> & {
  pausedChannels: PausedChannel[]
}

const MAX_NAMED_CHANNELS = 3

const SERVICE_DISPLAY_NAMES: Record<Service, string> = {
  bluesky: 'Bluesky',
  facebook: 'Facebook',
  googlebusiness: 'Google Business Profile',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  mastodon: 'Mastodon',
  pinterest: 'Pinterest',
  startPage: 'Start Page',
  substack: 'Substack',
  threads: 'Threads',
  tiktok: 'TikTok',
  twitter: 'Twitter / X',
  whatsapp: 'WhatsApp',
  youtube: 'YouTube',
}

const formatChannel = ({ name, service }: PausedChannel): string =>
  `${name} (${SERVICE_DISPLAY_NAMES[service]})`

export const formatHeading = (pausedChannels: PausedChannel[]): string => {
  if (pausedChannels.length === 1) {
    return `Your queue for ${formatChannel(pausedChannels[0])} is paused`
  }

  if (pausedChannels.length <= MAX_NAMED_CHANNELS) {
    const names = pausedChannels.map(formatChannel).join(', ')
    return `Some of your queues are paused: ${names}`
  }

  const firstTwo = pausedChannels.slice(0, 2).map(formatChannel).join(', ')
  const remaining = pausedChannels.length - 2
  return `Some of your queues are paused: ${firstTwo}, + ${remaining} others`
}

export const AllChannelsPausedQueueNotice = ({
  pausedChannels,
  ...props
}: AllChannelsPausedQueueNoticeProps): JSX.Element | null => {
  if (pausedChannels.length === 0) {
    return null
  }

  const isSingleChannel = pausedChannels.length === 1

  return (
    <Notice {...props} variant="warning">
      <Notice.Heading>{formatHeading(pausedChannels)}</Notice.Heading>
      <Notice.Text>
        {isSingleChannel
          ? 'Posts for this channel will only go out once the queue is resumed.'
          : 'Posts for these channels will only go out once the queue is resumed.'}
      </Notice.Text>
    </Notice>
  )
}
